import {hpcAttrs} from '@github-ui/hpc'
import {Box, Grid, ThemeProvider, Pagination, Text} from '@primer/react-brand/esm'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS} from '@contentful/rich-text-types'
import {ssrSafeLocation} from '@github-ui/ssr-utils'

import type {IndexPage} from '../../../schemas/contentful/contentTypes/indexTemplate'
import {ContentfulHero} from '../../../components/contentful/ContentfulHero/ContentfulHero'
import {ContentfulSubnav} from '../../../components/contentful/ContentfulSubnav/ContentfulSubnav'
import styles from './IndexTemplate.module.css'
import {IndexCard} from './components/IndexCard/IndexCard'
import {IndexFilters} from './components/IndexFilters/IndexFilters'
import type {CheckboxGroupProps} from './components/IndexFilters/IndexFilters'
import {useMemo} from 'react'
import {getAnalyticsEvent} from '../../../lib/utils/analytics'
import {appendFeatureFlagsToUrl} from '../../../lib/utils/urls'
import {IndexSort} from './components/IndexSort/IndexSort'
import type {SortOption} from './components/IndexSort/IndexSort'
import IndexSchema from './components/IndexStructuredData'

export type IndexTemplateProps = {
  page: IndexPage
  additionalProps?: {
    filters: CheckboxGroupProps[]
    totalPages: number
    sortOptions?: SortOption[]
    currentSort?: string
    showSpotlightCard?: boolean
  }
}

export function IndexTemplate({page, additionalProps}: IndexTemplateProps) {
  const {subNav, hero, cards, emptyStateDescription, spotlightCard} = page.fields.template.fields
  const {
    filters = [],
    totalPages = 0,
    sortOptions = [],
    currentSort = '',
    showSpotlightCard = false,
  } = additionalProps || {}

  const currentPage = useMemo(() => {
    const pageParam = new URLSearchParams(ssrSafeLocation.search).get('page')
    const parsed = Number(pageParam)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
  }, [])

  const getPaginationURL = (pageNumber: number): string => {
    const url = new URL(`${ssrSafeLocation.pathname}${ssrSafeLocation.search}`, ssrSafeLocation.origin)
    const queryParams = new URLSearchParams(ssrSafeLocation.search)
    const featureFlags = queryParams.get('_features') || ''

    url.searchParams.delete('_features')
    url.searchParams.set('page', pageNumber.toString())
    return appendFeatureFlagsToUrl(url.toString(), featureFlags)
  }

  const handleSortChange = (newValue: string) => {
    const url = new URL(window.location.href, window.location.origin)
    const queryParams = new URLSearchParams(ssrSafeLocation.search)
    const featureFlags = queryParams.get('_features') || ''

    url.searchParams.delete('page')
    url.searchParams.delete('_features')
    url.searchParams.set('sort', newValue)
    window.location.href = appendFeatureFlagsToUrl(url.toString(), featureFlags)
  }

  const hasResults = useMemo(() => {
    return cards && cards.length > 0
  }, [cards])

  const showSort = sortOptions.length > 1

  return (
    <ThemeProvider colorMode={page.fields.settings?.fields.colorMode ?? 'light'}>
      <IndexSchema page={page.fields.template} />
      {subNav ? <ContentfulSubnav component={subNav} /> : null}
      <Box paddingBlockEnd={{narrow: 40, regular: 80}}>
        <Grid className={styles.layoutGrid}>
          <Grid.Column span={12}>
            <Box marginBlockStart={{narrow: 64, regular: 112}} marginBlockEnd={{regular: 64}}>
              {hero ? (
                <ContentfulHero {...hpcAttrs()} component={hero} heroClassName="pt-0 pb-0" paddingBlockEnd="none" />
              ) : null}
            </Box>
          </Grid.Column>
          <Grid.Column span={{xsmall: 12, medium: 3}} className={styles.filtersColumn}>
            <IndexFilters checkboxGroups={filters} />
          </Grid.Column>
          <Grid.Column span={{xsmall: 12, medium: 9}}>
            {hasResults && showSort ? (
              <IndexSort sortOptions={sortOptions} currentSort={currentSort} onSortChange={handleSortChange} />
            ) : null}
            <Box marginBlockEnd={{narrow: 32, regular: 48}}>
              {hasResults ? (
                <Grid className={styles.cardGrid}>
                  {showSpotlightCard && spotlightCard ? (
                    <Grid.Column span={12} key={spotlightCard.sys.id}>
                      <IndexCard component={spotlightCard} className={styles.spotlightCard} index={3} />
                    </Grid.Column>
                  ) : null}
                  {cards?.map((card, index) => {
                    return (
                      <Grid.Column span={{xsmall: 12, medium: 6}} key={card.sys.id}>
                        <IndexCard component={card} index={index} colorMode={page.fields.settings?.fields.colorMode} />
                      </Grid.Column>
                    )
                  })}
                </Grid>
              ) : (
                <Box className={styles.emptyStateWrapper} role="status">
                  {emptyStateDescription
                    ? documentToReactComponents(emptyStateDescription, {
                        renderNode: {
                          [BLOCKS.PARAGRAPH]: (_, children) => (
                            <Text variant="muted" as="p">
                              {children}
                            </Text>
                          ),
                        },
                      })
                    : 'No results found. Please clear your filters and try another search.'}
                </Box>
              )}
            </Box>
            {totalPages > 1 ? (
              <Box paddingBlockStart={28} borderBlockStartWidth="thin" borderColor="default" borderStyle="solid">
                <Pagination
                  pageCount={totalPages}
                  currentPage={currentPage}
                  hrefBuilder={getPaginationURL}
                  pageAttributesBuilder={nextPageNumber => {
                    return getAnalyticsEvent({
                      action: `page_${nextPageNumber}`,
                      tag: 'link',
                      context: 'pagination',
                      location: 'category_page_cards',
                    })
                  }}
                  showPages
                />
              </Box>
            ) : null}
          </Grid.Column>
        </Grid>
      </Box>
    </ThemeProvider>
  )
}
